import React, { FC, useContext, useState } from 'react';
import { Typography } from '@material-ui/core';
import { Column, Filter, Query } from 'material-table';
import AddBoxIcon from '@material-ui/icons/AddBox';
import EditIcon from '@material-ui/icons/Edit';
import EnseigneFormsModal from './EnseigneForms';
import MaterialTableComponent from '../../common/components/MaterialTableComponent';
import { materialTableLocaleData } from '../../locale/material-table';
import { findSubstring, localCompareAICI } from '../../utils/common';
import { IUserContext, UserContext } from '../../providers/UserProvider';
import {
  Operation,
  PHYSICAL_MODEL,
  Resource,
  TYPE_ENSEIGN,
} from '../../common/constants';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import CSVImportEnseignesModal from './CSVImport/CSVImportEnseignesModal';
import MTableSelectFilterComponent from '../../common/components/MaterialTableComponents/MTableSelectFilterComponent';
import {
  BusinessOrderByInput,
  BusinessWhereInput,
  PhysicalModel,
  SortOrder,
  TypeEnseigne,
} from '../../types/graphql-global-types';
import MTableFilterComponent from '../../common/components/MaterialTableComponents/MTableFilterComponent';
import DeleteIcon from '@material-ui/icons/Delete';
import ConfirmationModal from '../../common/components/ConfirmationModal';
import { useDeleteEnseigne } from '../../hooks/mutations/deleteEnseigne';
import { formatSIREN } from '../../common/validator';
import { useApolloClient } from '@apollo/react-hooks';
import { GET_ALL_ENSEIGNES } from '../../graphql/enseigne/query';
import {
  AllEnseignes,
  AllEnseignes_allEnseignes_enseignes,
  AllEnseignesVariables,
} from '../../graphql/enseigne/types/AllEnseignes';
import { API_URL } from '../../config';

interface EnseignesProps {}

const Enseignes: FC<EnseignesProps> = () => {
  const client = useApolloClient();
  const { canI } = useContext<IUserContext>(UserContext);

  const canCreateEnseigne = canI(Operation.CREATE, Resource.ENSEIGNES);
  const canEditEnseigne = canI(Operation.UPDATE, Resource.ENSEIGNES);
  const canImportEnseigne = canI(Operation.IMPORT, Resource.ENSEIGNES);
  const canExportEnseigne = canI(Operation.EXPORT, Resource.ENSEIGNES);

  const [open, setOpen] = useState<boolean>(false);
  const [openCSVImport, setOpenCSVImport] = useState<boolean>(false);
  const [
    selectedEnseigne,
    setSelectedEnseigne,
  ] = useState<AllEnseignes_allEnseignes_enseignes>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const [deleteEnseigne] = useDeleteEnseigne();

  const handleCloseCSVImport = () => {
    setOpenCSVImport(false);
  };

  const handleDelete = () => {
    if (selectedEnseigne)
      deleteEnseigne({
        variables: { input: { id: selectedEnseigne?.id } },
      }).then(() => {
        setSelectedEnseigne(undefined);
        setOpenDeleteDialog(false);
      });
  };

  return (
    <>
      <MaterialTableComponent
        title="Liste des enseignes"
        actions={[
          {
            icon: () => <GetAppIcon />,
            onClick: (_, __) => {
              window.open(API_URL + '/api/v1/download/enseignes');
            },
            isFreeAction: true,
            tooltip: 'Exporter sous Excel',
            disabled: !canExportEnseigne,
          },
          {
            icon: () => <PublishIcon />,
            onClick: (_, __) => {
              setOpenCSVImport(true);
            },
            isFreeAction: true,
            tooltip: 'Importer depuis un fichier CSV',
            disabled: !canImportEnseigne,
          },
          {
            icon: () => <AddBoxIcon />,
            onClick: (_, __) => {
              setOpen(true);
              setSelectedEnseigne(undefined);
            },
            isFreeAction: true,
            tooltip: 'Créer une nouvelle enseigne',
            disabled: !canCreateEnseigne,
          },
          {
            icon: () => <EditIcon />,
            tooltip: 'Editer',
            onClick: (event, rowData) => {
              setSelectedEnseigne(rowData);
              setOpen(true);
            },
            disabled: !canEditEnseigne,
          },
          (rowData: AllEnseignes_allEnseignes_enseignes) => ({
            icon: () => <DeleteIcon />,
            tooltip: !rowData?.linkedWithSomething ? 'Supprimer' : undefined,
            onClick: (event, rowData) => {
              setSelectedEnseigne(rowData);
              setOpenDeleteDialog(true);
            },
            disabled: rowData?.linkedWithSomething ?? false,
          }),
        ]}
        columns={tableColumns}
        data={(query) =>
          new Promise(async (resolve) => {
            const { page, pageSize } = query;
            const search = query?.search?.trim() || undefined;

            const filters = query?.filters?.reduce<BusinessWhereInput>(
              (acc, filter) => ({
                ...acc,
                ...formatFilter(filter),
              }),
              {},
            );

            const { data } = await client.query<
              AllEnseignes,
              AllEnseignesVariables
            >({
              fetchPolicy: 'network-only',
              query: GET_ALL_ENSEIGNES,
              variables: {
                input: {
                  take: pageSize,
                  skip: page * pageSize,
                  where: {
                    name: search,
                    phone: search,
                    email: search,
                    ...filters,
                  },
                  orderBy: formatOrderBy(query),
                },
              },
            });

            const totalCount = data?.allEnseignes?.totalCount ?? 0;
            const businesses = data?.allEnseignes?.enseignes ?? [];

            resolve({
              totalCount,
              page,
              data: businesses,
            });
          })
        }
        isLoading={false}
        localization={{
          ...materialTableLocaleData.fr,
          header: {
            actions: 'Actions',
          },
        }}
        options={{
          filtering: true,
          grouping: false,
          pageSize: 10,
        }}
      />

      {open && (
        <EnseigneFormsModal
          title="Création enseigne"
          loading={isLoading}
          open={open}
          setIsLoading={setIsLoading}
          onCancel={() => setOpen(false)}
          onCreate={() => setOpen(false)}
          selectedEnseigne={selectedEnseigne}
        />
      )}

      {openCSVImport && (
        <CSVImportEnseignesModal handleClose={handleCloseCSVImport} />
      )}
      <ConfirmationModal
        content={
          <Typography>
            Voulez-vous vraiment supprimer cette enseigne ?
          </Typography>
        }
        confirmLabel="Oui, supprimer"
        open={openDeleteDialog}
        onConfirm={handleDelete}
        onCancel={() => {
          setSelectedEnseigne(undefined);
          setOpenDeleteDialog(false);
        }}
      />
    </>
  );
};

const tableColumns = [
  {
    title: 'ID',
    editable: 'never',
    field: 'id',
    searchable: false,
    width: 80,
    filterComponent: ({ columnDef, onFilterChanged }: any) => (
      <MTableFilterComponent
        onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
      />
    ),
    customFilterAndSearch: (
      filter: any,
      rowData: AllEnseignes_allEnseignes_enseignes,
      _: Column<AllEnseignes_allEnseignes_enseignes>,
    ): boolean => `${rowData?.id}` === filter,
  },
  {
    title: 'Nom',
    field: 'name',
    emptyValue: '-',
    defaultGroupSort: 'asc',
    defaultSort: 'asc',
    filtering: false,
    customSort: (
      rowData1: AllEnseignes_allEnseignes_enseignes,
      rowData2: AllEnseignes_allEnseignes_enseignes,
    ) => localCompareAICI(rowData1?.name, rowData2?.name),
    customFilterAndSearch: (
      filter: string,
      rowData: AllEnseignes_allEnseignes_enseignes,
      _: Column<AllEnseignes_allEnseignes_enseignes>,
    ): boolean => findSubstring(rowData?.name, filter),
  },
  {
    title: 'Type',
    field: 'type',
    lookup: TYPE_ENSEIGN,
    searchable: false,
    customFilterAndSearch: (
      filter: string,
      rowData: AllEnseignes_allEnseignes_enseignes,
      _: Column<AllEnseignes_allEnseignes_enseignes>,
    ): boolean => rowData.type === filter,
    filterComponent: ({ columnDef, onFilterChanged }: any) => {
      return (
        <MTableSelectFilterComponent
          options={Object.keys(TypeEnseigne)}
          labels={TYPE_ENSEIGN}
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      );
    },
  },
  {
    title: 'Modèle physique',
    field: 'physicalModel',
    lookup: PHYSICAL_MODEL,
    searchable: false,
    customFilterAndSearch: (
      filter: string,
      rowData: AllEnseignes_allEnseignes_enseignes,
      _: Column<AllEnseignes_allEnseignes_enseignes>,
    ): boolean => rowData.physicalModel === filter,
    filterComponent: ({ columnDef, onFilterChanged }: any) => {
      return (
        <MTableSelectFilterComponent
          options={Object.keys(PhysicalModel)}
          labels={PHYSICAL_MODEL}
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      );
    },
  },
  {
    title: 'Raison sociale',
    field: 'socialReason',
    filtering: false,
    searchable: false,
    emptyValue: '-',
    filterComponent: ({ columnDef, onFilterChanged }: any) => (
      <MTableFilterComponent
        onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
      />
    ),
    customFilterAndSearch: (
      filter: any,
      rowData: AllEnseignes_allEnseignes_enseignes,
      _: Column<AllEnseignes_allEnseignes_enseignes>,
    ): boolean => findSubstring(rowData?.socialReason, filter),
  },
  {
    title: 'Adresse',
    field: 'address',
    emptyValue: '-',
    searchable: false,
    filterComponent: ({ columnDef, onFilterChanged }: any) => (
      <MTableFilterComponent
        onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
      />
    ),
    customFilterAndSearch: (
      filter: any,
      rowData: AllEnseignes_allEnseignes_enseignes,
      _: Column<AllEnseignes_allEnseignes_enseignes>,
    ): boolean => findSubstring(rowData?.address, filter),
  },
  {
    title: 'Code Postal',
    field: 'zipCode',
    emptyValue: '-',
    searchable: false,
    filterComponent: ({ columnDef, onFilterChanged }: any) => (
      <MTableFilterComponent
        onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
      />
    ),
    customFilterAndSearch: (
      filter: any,
      rowData: AllEnseignes_allEnseignes_enseignes,
      _: Column<AllEnseignes_allEnseignes_enseignes>,
    ): boolean => rowData?.zipCode === filter,
  },
  {
    title: 'Ville',
    field: 'city',
    emptyValue: '-',
    searchable: false,
    filterComponent: ({ columnDef, onFilterChanged }: any) => (
      <MTableFilterComponent
        onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
      />
    ),
    customFilterAndSearch: (
      filter: string,
      rowData: AllEnseignes_allEnseignes_enseignes,
      _: Column<AllEnseignes_allEnseignes_enseignes>,
    ): boolean => findSubstring(rowData?.city, filter),
  },
  {
    title: 'Email',
    field: 'email',
    emptyValue: '-',
    filtering: false,
  },
  {
    title: 'Téléphone',
    field: 'phone',
    emptyValue: '-',
    filtering: false,
  },
  // {
  //   title: 'Pays',
  //   field: 'country',
  //   emptyValue: '-',
  // },
  {
    title: 'SIREN',
    field: 'SIREN',
    render: (dataRow) => (dataRow.SIREN ? formatSIREN(dataRow.SIREN) : '-'),
    filtering: false,
    searchable: false,
  },
  {
    title: 'IBAN',
    field: 'IBAN',
    emptyValue: '-',
    filtering: false,
    searchable: false,
  },
] as Array<Column<AllEnseignes_allEnseignes_enseignes>>;

const formatFilter = (filter: Filter<any>): BusinessWhereInput => {
  const key = filter?.column?.field as string;
  const value = filter?.value || undefined;

  const _parseNumber = (n: string | null | undefined): number | undefined =>
    n && !isNaN(+n) ? +n : undefined;

  const mapping: { [p: string]: any } = {
    id: { idCC: _parseNumber(value) },
    idCRM: { idCRM: _parseNumber(value) },
  };

  return mapping[key] || { [key]: value };
};

const formatOrderBy = (query: Query<any>): BusinessOrderByInput | undefined => {
  const key = query?.orderBy?.field;
  const direction = query?.orderDirection as SortOrder;

  if (!key || !direction) return undefined;

  const mapping: { [p: string]: any } = {
    id: { idCC: direction },
  };

  return mapping[key as string] || { [key]: direction };
};

export default Enseignes;
